import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface Goal {
  id: string;
  title: string;
}

interface AddNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  carePlanId: string;
  goals: Goal[];
  onSuccess: () => void;
}

export function AddNoteDialog({ open, onOpenChange, carePlanId, goals, onSuccess }: AddNoteDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [note, setNote] = useState("");
  const [goalId, setGoalId] = useState<string | undefined>();

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Not authenticated");
      if (!note.trim()) throw new Error("Please enter a note");

      const { error } = await supabase.from("care_plan_notes").insert({
        care_plan_id: carePlanId,
        provider_id: user.id,
        goal_id: goalId || null,
        note: note.trim(),
      });

      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Note added successfully" });
      onSuccess();
      onOpenChange(false);
      setNote("");
      setGoalId(undefined);
    },
    onError: (error) => {
      toast({ title: "Error adding note", description: error.message, variant: "destructive" });
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Add Progress Note</DialogTitle>
          <DialogDescription>
            Document progress, observations, or updates for this care plan
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Related Goal (optional) */}
          {goals.length > 0 && (
            <div className="space-y-2">
              <Label>Related Goal (optional)</Label>
              <Select value={goalId} onValueChange={setGoalId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a goal..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No specific goal</SelectItem>
                  {goals.map(goal => (
                    <SelectItem key={goal.id} value={goal.id}>
                      {goal.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Note */}
          <div className="space-y-2">
            <Label htmlFor="note">Note *</Label>
            <Textarea
              id="note"
              placeholder="Enter your progress note..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending || !note.trim()}
          >
            {createMutation.isPending ? "Adding..." : "Add Note"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
